import { useMemo } from 'react';

import { gql } from '@apollo/client';
import { useQuery } from '@apollo/client/react';
import { format } from 'date-fns';
import { useSelector } from 'react-redux';

import { selectAnalyticsDates, selectFilters } from '@/modules/analytics/redux/selectors';
import { getComparisonDates } from '@/modules/analytics/utils/getComparisonDates';
import { selectAccountId } from '@/redux/session/selectors';
import { AccountAnalytics } from '@/shared/types/analytics';
import { RootState } from '@/shared/types/state';
import { getAnalyticsRangeUTC } from '@/util/analytics';
import { getGraphQLSet } from '@/util/util';

import { useComparisonMode } from '../../../../hooks/useComparisonMode';
import { ratingsFilterToSet } from '../../../../utils/expandRatings';

export type DateTimeSeriesPoint = { label: string; value: number };

export interface ContactInsight {
  insightName: string;
  collectedCount: number;
  totalCount: number;
  /** Heuristic — `email` if the insight name contains "email", else `phone_number`. */
  kind: 'email' | 'phone_number';
}

export interface BooleanInsight {
  insightName: string;
  trueCount: number;
  totalCount: number;
  truePercent: number;
}

export interface CategoricalBucket {
  id: string;
  label: string;
  count: number;
  /** 0–100 share of `totalCount`. */
  value: number;
}

export interface CategoricalInsight {
  insightName: string;
  totalCount: number;
  buckets: CategoricalBucket[];
}

export interface DateTimeInsight {
  insightName: string;
  totalCount: number;
  series: DateTimeSeriesPoint[];
}

export interface SingleAgentCustomInsightsData {
  contact: ContactInsight[];
  boolean: BooleanInsight[];
  categorical: CategoricalInsight[];
  datetime: DateTimeInsight[];
  previousContactByName: Map<string, ContactInsight>;
  previousBooleanByName: Map<string, BooleanInsight>;
}

interface BucketRow {
  insight_name: string | null;
  buckets: Record<string, number> | null;
  total_count: number | null;
}

interface BooleanRow {
  insight_name: string | null;
  true_count: number | null;
  total_count: number | null;
}

interface ContactRow {
  insight_name: string | null;
  collected_count: number | null;
  total_count: number | null;
}

const CUSTOM_INSIGHTS_ARGS = `
  start_time:      $start_time
  end_time:        $end_time
  brain_parent_id: $brain_parent_id
  desk_ids:        $desk_ids
  channels:        $channels
  is_proactive:    $is_proactive
  is_covered:      $is_covered
  csat_brain:      $csat_brain
  is_test:         $is_test
`;

const CUSTOM_INSIGHTS_PARAMS = `
  $start_time: timestamp!
  $end_time: timestamp!
  $brain_parent_id: uuid!
  $desk_ids: _uuid
  $channels: _text
  $is_proactive: Boolean
  $is_covered: Boolean
  $csat_brain: _int4
  $is_test: Boolean
`;

const buildInsightQuery = (opName: string, table: string, fields: string) => gql`
  query ${opName}(${CUSTOM_INSIGHTS_PARAMS}) {
    rows: ${table}(args: { ${CUSTOM_INSIGHTS_ARGS} }) {
      ${fields}
    }
  }
`;

const BUCKET_FIELDS = 'insight_name buckets total_count';

// Distinct operation names so Apollo's cache keeps the current and
// comparison windows separated — same trick the rest of the AI Agents
// hooks use.
const CATEGORICAL_QUERY = buildInsightQuery(
  'AiAgentsCustomInsightsCategorical',
  'ai_agents_custom_insights_categorical',
  BUCKET_FIELDS
);
const DATETIME_QUERY = buildInsightQuery(
  'AiAgentsCustomInsightsDatetime',
  'ai_agents_custom_insights_datetime',
  BUCKET_FIELDS
);
const BOOLEAN_QUERY = buildInsightQuery(
  'AiAgentsCustomInsightsBoolean',
  'ai_agents_custom_insights_boolean',
  'insight_name true_count total_count'
);
const BOOLEAN_QUERY_COMPARISON = buildInsightQuery(
  'AiAgentsCustomInsightsBooleanComparison',
  'ai_agents_custom_insights_boolean',
  'insight_name true_count total_count'
);
const CONTACT_QUERY = buildInsightQuery(
  'AiAgentsCustomInsightsContact',
  'ai_agents_custom_insights_contact',
  'insight_name collected_count total_count'
);
const CONTACT_QUERY_COMPARISON = buildInsightQuery(
  'AiAgentsCustomInsightsContactComparison',
  'ai_agents_custom_insights_contact',
  'insight_name collected_count total_count'
);

const selectSelectedBrainId = (state: RootState): string | null => {
  const filter = state.analytics.accountAnalytics?.find((f) => f.type === 'brainIds');
  const value = filter?.brainIds?.[0]?.value;
  return typeof value === 'string' && value.length > 0 ? value : null;
};

interface CustomInsightsVariables {
  start_time: string;
  end_time: string;
  brain_parent_id: string;
  // _uuid / _text / _int4 SQL-function args: serialize via getGraphQLSet to a
  // Postgres array literal string ("{a,b}"), never a raw JSON array.
  desk_ids?: string;
  channels?: string;
  is_proactive?: boolean;
  is_covered?: boolean;
  csat_brain?: string;
  is_test?: boolean;
}

interface BuildCustomInsightsVariablesArgs {
  brainId: string | null;
  startDate: string | undefined;
  endDate: string | undefined;
  analyticsFilters: AccountAnalytics[];
}

export const buildCustomInsightsVariables = ({
  brainId,
  startDate,
  endDate,
  analyticsFilters,
}: BuildCustomInsightsVariablesArgs): CustomInsightsVariables | null => {
  if (!brainId) {
    return null;
  }
  const range = getAnalyticsRangeUTC(startDate, endDate);
  if (!range.startDate || !range.endDate) {
    return null;
  }

  const vars: CustomInsightsVariables = {
    start_time: range.startDate,
    end_time: range.endDate,
    brain_parent_id: brainId,
  };

  for (const filter of analyticsFilters ?? []) {
    switch (filter.type) {
      case 'deskIds': {
        const ids = (filter.deskIds ?? []).map((o) => o.value).filter(Boolean) as string[];
        const set = getGraphQLSet(ids);
        if (set) {
          vars.desk_ids = set;
        }
        break;
      }
      case 'channels': {
        const set = getGraphQLSet(filter.channels ?? []);
        if (set) {
          vars.channels = set;
        }
        break;
      }
      case 'isTest':
        if (typeof filter.isTest === 'boolean') {
          vars.is_test = filter.isTest;
        }
        break;
      case 'firstMessageType':
        if (filter.firstMessageType === 'proactive') {
          vars.is_proactive = true;
        } else if (filter.firstMessageType === 'reactive') {
          vars.is_proactive = false;
        }
        break;
      case 'ratings': {
        const csat = ratingsFilterToSet(filter.ratings, filter.ratingsMode);
        if (csat) {
          vars.csat_brain = csat;
        }
        break;
      }
      default:
        break;
    }
  }

  return vars;
};

const useCustomInsightsVariables = (
  brainId: string | null,
  dateOverride?: { startDate: string; endDate: string }
): { variables: CustomInsightsVariables | null; skip: boolean } => {
  const accountId = useSelector(selectAccountId);
  const filters = useSelector(selectFilters);

  const effectiveStart = dateOverride?.startDate ?? filters.startDate;
  const effectiveEnd = dateOverride?.endDate ?? filters.endDate;

  const variables = useMemo<CustomInsightsVariables | null>(
    () =>
      buildCustomInsightsVariables({
        brainId,
        startDate: effectiveStart ?? undefined,
        endDate: effectiveEnd ?? undefined,
        analyticsFilters: filters.analytics ?? [],
      }),
    [brainId, effectiveStart, effectiveEnd, filters.analytics]
  );

  const skip = !accountId || !filters.filtersLoaded || !variables;
  return { variables, skip };
};

const detectContactKind = (insightName: string): 'email' | 'phone_number' => {
  const lower = insightName.toLowerCase();
  if (lower.includes('phone') || lower.includes('number') || lower.includes('mobile')) {
    return 'phone_number';
  }
  return 'email';
};

const toContact = (rows: ContactRow[] | undefined): ContactInsight[] =>
  (rows ?? [])
    .filter((row): row is ContactRow & { insight_name: string } => Boolean(row.insight_name))
    .map((row) => ({
      insightName: row.insight_name,
      collectedCount: row.collected_count ?? 0,
      totalCount: row.total_count ?? 0,
      kind: detectContactKind(row.insight_name),
    }));

const toBoolean = (rows: BooleanRow[] | undefined): BooleanInsight[] =>
  (rows ?? [])
    .filter((row): row is BooleanRow & { insight_name: string } => Boolean(row.insight_name))
    .map((row) => {
      const trueCount = row.true_count ?? 0;
      const totalCount = row.total_count ?? 0;
      const truePercent = totalCount > 0 ? Math.round((trueCount / totalCount) * 100) : 0;
      return { insightName: row.insight_name, trueCount, totalCount, truePercent };
    });

const toCategorical = (rows: BucketRow[] | undefined): CategoricalInsight[] =>
  (rows ?? [])
    .filter((row): row is BucketRow & { insight_name: string } => Boolean(row.insight_name))
    .map((row) => {
      const totalCount = row.total_count ?? 0;
      const entries = Object.entries(row.buckets ?? {}).sort((a, b) => b[1] - a[1]);
      const buckets: CategoricalBucket[] = entries.map(([label, count]) => ({
        id: label,
        label,
        count,
        value: totalCount > 0 ? Math.round((count / totalCount) * 1000) / 10 : 0,
      }));
      return { insightName: row.insight_name, totalCount, buckets };
    });

const formatDateTimeBucketLabel = (raw: string): string => {
  const parsed = new Date(raw);
  if (!Number.isNaN(parsed.getTime())) {
    return format(parsed, 'MMM d');
  }
  return raw;
};

const toDateTime = (rows: BucketRow[] | undefined): DateTimeInsight[] =>
  (rows ?? [])
    .filter((row): row is BucketRow & { insight_name: string } => Boolean(row.insight_name))
    .map((row) => {
      const totalCount = row.total_count ?? 0;
      const entries = Object.entries(row.buckets ?? {}).sort(
        ([a], [b]) => new Date(a).getTime() - new Date(b).getTime()
      );
      const series: DateTimeSeriesPoint[] = entries.map(([rawLabel, count]) => ({
        label: formatDateTimeBucketLabel(rawLabel),
        value: count,
      }));
      return { insightName: row.insight_name, totalCount, series };
    });

const indexByName = <T extends { insightName: string }>(items: T[]): Map<string, T> =>
  new Map(items.map((item) => [item.insightName, item]));

const EMPTY_DATA: SingleAgentCustomInsightsData = {
  contact: [],
  boolean: [],
  categorical: [],
  datetime: [],
  previousContactByName: new Map(),
  previousBooleanByName: new Map(),
};

export const useSingleAgentCustomInsights = () => {
  const { isComparing } = useComparisonMode();
  const { startDate, endDate } = useSelector(selectAnalyticsDates);
  const brainId = useSelector(selectSelectedBrainId);

  const comparisonDates = useMemo(
    () => getComparisonDates(startDate, endDate),
    [startDate, endDate]
  );
  const { variables: currentVariables, skip } = useCustomInsightsVariables(brainId);
  const { variables: comparisonVariables } = useCustomInsightsVariables(brainId, comparisonDates);
  const skipCompare = skip || !isComparing || !comparisonVariables;

  const categoricalQ = useQuery<{ rows: BucketRow[] }>(CATEGORICAL_QUERY, {
    variables: currentVariables ?? undefined,
    skip,
  });
  const datetimeQ = useQuery<{ rows: BucketRow[] }>(DATETIME_QUERY, {
    variables: currentVariables ?? undefined,
    skip,
  });
  const booleanQ = useQuery<{ rows: BooleanRow[] }>(BOOLEAN_QUERY, {
    variables: currentVariables ?? undefined,
    skip,
  });
  const contactQ = useQuery<{ rows: ContactRow[] }>(CONTACT_QUERY, {
    variables: currentVariables ?? undefined,
    skip,
  });

  const booleanCompareQ = useQuery<{ rows: BooleanRow[] }>(BOOLEAN_QUERY_COMPARISON, {
    variables: comparisonVariables ?? undefined,
    skip: skipCompare,
  });
  const contactCompareQ = useQuery<{ rows: ContactRow[] }>(CONTACT_QUERY_COMPARISON, {
    variables: comparisonVariables ?? undefined,
    skip: skipCompare,
  });

  const data = useMemo<SingleAgentCustomInsightsData>(() => {
    if (skip) {
      return EMPTY_DATA;
    }
    return {
      contact: toContact(contactQ.data?.rows),
      boolean: toBoolean(booleanQ.data?.rows),
      categorical: toCategorical(categoricalQ.data?.rows),
      datetime: toDateTime(datetimeQ.data?.rows),
      previousContactByName: indexByName(toContact(contactCompareQ.data?.rows)),
      previousBooleanByName: indexByName(toBoolean(booleanCompareQ.data?.rows)),
    };
  }, [
    skip,
    contactQ.data?.rows,
    booleanQ.data?.rows,
    categoricalQ.data?.rows,
    datetimeQ.data?.rows,
    contactCompareQ.data?.rows,
    booleanCompareQ.data?.rows,
  ]);

  const isLoading =
    categoricalQ.loading ||
    datetimeQ.loading ||
    booleanQ.loading ||
    contactQ.loading ||
    (isComparing && (booleanCompareQ.loading || contactCompareQ.loading));

  const error =
    categoricalQ.error ??
    datetimeQ.error ??
    booleanQ.error ??
    contactQ.error ??
    booleanCompareQ.error ??
    contactCompareQ.error ??
    null;

  return { data, isLoading, error };
};
